import {
  Box,
  ChevronDown,
  ChevronRight,
  Database,
  ListTree,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { appendBlock } from "../state/graph-actions";
import { useBlock, useHierarchyIndex } from "../state/graph-hooks";
import { useInteractionStore } from "../state/interaction-store";
import { useUIStore } from "../state/ui-store";
import { useYDoc } from "../state/yjs-provider";
import { IconButton } from "./icon-button";

function TreeRow({
  id,
  depth = 0,
  childIdsByParent,
}: {
  id: string;
  depth?: number;
  childIdsByParent: Record<string, string[]>;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const block = useBlock(id);
  const { hovered, selection, setHovered, toggleSelection } =
    useInteractionStore();
  const setViewMode = useUIStore((s) => s.setViewMode);
  const doc = useYDoc();

  if (!block) return null;

  const children = childIdsByParent[id] || [];
  const isSelected = selection.includes(id);
  const isHovered = hovered === id;

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSelection(id);
    if (e.detail === 2) {
      setViewMode("document");
    }
  };

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    appendBlock(
      doc,
      { type: "text", name: "Untitled", content: "" },
      { parentId: id },
    );
    setIsExpanded(true);
  };

  return (
    <div className="flex flex-col gap-px select-none">
      <div
        className={`group flex items-center gap-1 py-[3px] pl-1 pr-1.5 cursor-pointer transition-colors rounded pointer-events-auto shrink-0 w-max text-[11px] font-medium ${
          isSelected
            ? "bg-white/15 text-white"
            : isHovered
              ? "bg-white/8 text-white/90"
              : "bg-white/5 text-white/70 hover:bg-white/8"
        }`}
        style={{ marginLeft: `${depth * 12}px` }}
        onMouseEnter={() => setHovered(id)}
        onMouseLeave={() => setHovered(null)}
        onClick={handleSelect}
      >
        {children.length > 0 ? (
          <IconButton
            type="button"
            tooltip={isExpanded ? "Collapse" : "Expand"}
            className="flex-shrink-0 flex items-center justify-center opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </IconButton>
        ) : (
          <Box className="w-3 h-3 opacity-30 flex-shrink-0" />
        )}

        <span className="truncate max-w-[140px]">
          {block.name || <span className="opacity-40 italic">Untitled</span>}
        </span>

        {(isHovered || isSelected) && (
          <IconButton
            type="button"
            tooltip="Add child"
            className="ml-0.5 opacity-40 hover:opacity-100 hover:bg-white/10 rounded p-px transition-all"
            onClick={handleAddChild}
          >
            <Plus className="w-3 h-3" />
          </IconButton>
        )}
      </div>

      {isExpanded && children.length > 0 && (
        <div className="flex flex-col gap-px">
          {children.map((childId) => (
            <TreeRow
              key={childId}
              id={childId}
              depth={depth + 1}
              childIdsByParent={childIdsByParent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function LeftPanel() {
  const hierarchy = useHierarchyIndex();
  const doc = useYDoc();
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const [isTreePinned, setIsTreePinned] = useState(false);
  const [isTreeHoverOpen, setIsTreeHoverOpen] = useState(false);

  const isTreeOpen = isTreePinned || isTreeHoverOpen;

  const modes = [
    { id: "scene" as const, icon: Box, label: "Scene" },
    { id: "board" as const, icon: Database, label: "Board" },
    { id: "document" as const, icon: FileTextIcon, label: "Document" },
  ];

  return (
    <div className="absolute top-2 left-2 z-40 pointer-events-none flex flex-col items-start">
      {/* View mode toggle group + add button + tree toggle */}
      <div
        className="pointer-events-auto flex items-center gap-px mb-1 rounded bg-black/40 backdrop-blur-sm border border-white/10 p-[2px]"
        onMouseLeave={() => setIsTreeHoverOpen(false)}
      >
        <IconButton
          tooltip={isTreePinned ? "Tree pinned open" : "Tree view"}
          className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
            isTreeOpen
              ? "bg-white/15 text-white"
              : "text-white/40 hover:text-white/70 hover:bg-white/8"
          }`}
          onMouseEnter={() => setIsTreeHoverOpen(true)}
          onClick={() => setIsTreePinned((prev) => !prev)}
        >
          <ListTree className="w-3 h-3" />
        </IconButton>

        <div className="w-px h-3 bg-white/10 mx-0.5" />

        {modes.map(({ id, icon: Icon, label }) => (
          <IconButton
            key={id}
            tooltip={label}
            className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
              viewMode === id
                ? "bg-white/15 text-white"
                : "text-white/40 hover:text-white/70 hover:bg-white/8"
            }`}
            onClick={() => setViewMode(id)}
          >
            <Icon className="w-3 h-3" />
          </IconButton>
        ))}

        <div className="w-px h-3 bg-white/10 mx-0.5" />

        <IconButton
          tooltip="Add node"
          className="flex items-center justify-center w-5 h-5 rounded text-white/40 hover:text-white/70 hover:bg-white/8 transition-colors"
          onClick={() =>
            appendBlock(doc, { type: "text", name: "Untitled", content: "" })
          }
        >
          <Plus className="w-3 h-3" />
        </IconButton>
      </div>

      {/* Tree rows */}
      {isTreeOpen && (
        <div
          className="flex flex-col gap-px max-h-[calc(100vh-4rem)] overflow-y-auto pr-2 pb-4"
          onMouseEnter={() => setIsTreeHoverOpen(true)}
          onMouseLeave={() => setIsTreeHoverOpen(false)}
        >
          {hierarchy.rootIds.map((id) => (
            <TreeRow
              key={id}
              id={id}
              childIdsByParent={hierarchy.childIdsByParent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileTextIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
